const SHARE_GUEST_ID_STORAGE_KEY = "bollyrhythm_share_guest_id_v1";
const SHARE_GUEST_ID_COOKIE_NAME = "bollyrhythm_share_guest_id_v1";
const SHARE_GUEST_ID_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

let inMemoryShareGuestId: string | null = null;

function isValidShareGuestId(value: string | null | undefined): value is string {
  return typeof value === "string" && value.trim().length >= 12 && value.trim().length <= 200;
}

function readShareGuestIdFromCookie() {
  if (typeof document === "undefined") return null;

  for (const part of document.cookie.split(";")) {
    const [rawName, ...rest] = part.trim().split("=");
    if (rawName !== SHARE_GUEST_ID_COOKIE_NAME) continue;

    const rawValue = rest.join("=");
    const value = rawValue ? decodeURIComponent(rawValue).trim() : "";
    return isValidShareGuestId(value) ? value : null;
  }

  return null;
}

function writeShareGuestIdToCookie(value: string) {
  if (typeof document === "undefined") return;

  document.cookie = `${SHARE_GUEST_ID_COOKIE_NAME}=${encodeURIComponent(value)}; Max-Age=${SHARE_GUEST_ID_COOKIE_MAX_AGE}; Path=/; SameSite=Lax`;
}

function readShareGuestIdFromStorage() {
  if (typeof window === "undefined") return null;

  try {
    const value = window.localStorage.getItem(SHARE_GUEST_ID_STORAGE_KEY)?.trim() ?? null;
    return isValidShareGuestId(value) ? value : null;
  } catch {
    return null;
  }
}

function writeShareGuestIdToStorage(value: string) {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(SHARE_GUEST_ID_STORAGE_KEY, value);
  } catch {
    // Cookie and in-memory fallbacks still preserve a browser-level identifier.
  }
}

function createShareGuestId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  return `share-guest-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/**
 * Returns a stable browser-level guest id for share tracking so repeated opens
 * from the same browser do not count as new unique guests when the network IP changes.
 */
export function getOrCreateShareGuestId() {
  if (typeof window === "undefined") return null;

  const existing =
    readShareGuestIdFromStorage() ?? readShareGuestIdFromCookie() ?? inMemoryShareGuestId;

  if (isValidShareGuestId(existing)) {
    inMemoryShareGuestId = existing;
    writeShareGuestIdToStorage(existing);
    writeShareGuestIdToCookie(existing);
    return existing;
  }

  const created = createShareGuestId();
  inMemoryShareGuestId = created;
  writeShareGuestIdToStorage(created);
  writeShareGuestIdToCookie(created);
  return created;
}
